import { User } from "firebase/auth";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ClubBadge from "./ClubBadge";
import { getClubBadge } from "../data/clubBadges";
import { getNationalityBadge } from "../data/nationalityBadges";
import {
  card,
  secondaryBtn,
  inputStyle,
  selectStyle,
  tableStyle,
  thStyle,
  tdStyle,
} from "../styles";

const PAGE_SIZE = 25;

export interface SelectedPlayer {
  id: string;
  name: string;
  club: string;
  position: string | null;
  goals?: number;
}

const actionBtnBase: React.CSSProperties = {
  fontSize: "0.72rem",
  borderRadius: 6,
  padding: "0.28rem 0.5rem",
  cursor: "pointer",
  fontFamily: "var(--font-body)",
  fontWeight: 600,
  whiteSpace: "nowrap",
  border: "none",
};

interface Props {
  user: User;
  competitionId: string;
  /** Players currently selected for this slot/GW — drives "isSelected" in modal */
  selectedPlayers: SelectedPlayer[];
  /** Called when adding a new player (or replacing current pick) */
  onSelect: (player: SelectedPlayer) => void;
  isInternational?: boolean;
  /** Label for the Add button, e.g. "Add to GW3" */
  addLabel?: string;
  /** Label for the Remove button when this is the current pick, e.g. "Remove from GW3" */
  removeLabel?: string;
  /** Label for the Move button when player is in another GW, e.g. "Move to GW3" */
  moveToLabel?: string;
  /** playerId → GW display name for players used in OTHER gameweeks */
  gwPickMap?: Record<string, string>;
  /** playerId → gwId for players used in OTHER gameweeks (for API calls) */
  gwPickIdMap?: Record<string, string>;
  /** Player IDs in other GWs that are locked — show GW label but no remove/move buttons */
  lockedGwPlayerIds?: string[];
  /** Sort players by goals scored in competition (descending) */
  sortByGoals?: boolean;
  /** Remove the current pick from this slot/GW */
  onRemoveCurrent?: (playerId: string) => void;
  /** Remove a player from whichever other GW they're in */
  onRemoveFromOtherGw?: (playerId: string) => void;
  /** Move a player from their other GW into this one */
  onMoveToThisGw?: (playerId: string) => void;
}

type PlayerRow = SelectedPlayer & { goals: number };

function PlayerPicker({
  user,
  competitionId,
  selectedPlayers,
  onSelect,
  isInternational = false,
  addLabel,
  removeLabel,
  moveToLabel,
  gwPickMap = {},
  gwPickIdMap = {},
  lockedGwPlayerIds = [],
  sortByGoals = false,
  onRemoveCurrent,
  onRemoveFromOtherGw,
  onMoveToThisGw,
}: Props) {
  const { t } = useTranslation();

  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [clubFilter, setClubFilter] = useState("");
  const [clubs, setClubs] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ── Fetch clubs once ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!competitionId) return;
    const fetchClubs = async () => {
      try {
        const token = await user.getIdToken();
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/players/clubs?competitionId=${competitionId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.ok) setClubs(await res.json());
      } catch {
        // non-fatal
      }
    };
    fetchClubs();
  }, [competitionId, user]);

  // ── Debounce search ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (search.length === 1) return;
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 2000);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  // ── Fetch players ─────────────────────────────────────────────────────────────
  const fetchPlayers = useCallback(async () => {
    if (!competitionId) return;
    setLoading(true);
    setError("");
    try {
      const token = await user.getIdToken();
      const params = new URLSearchParams({
        competitionId,
        page: String(page),
        pageSize: String(PAGE_SIZE),
      });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (clubFilter) params.set("club", clubFilter);
      if (sortByGoals) params.set("sort", "goals");

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/players?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        const data = await res.json();
        setPlayers(data.players.map((p: any) => ({ ...p, goals: p.goals ?? 0 })));
        setTotal(data.total);
      } else {
        setError(t("playerSelection.errors.couldNotLoadPlayers"));
      }
    } catch {
      setError(t("common.somethingWentWrong"));
    } finally {
      setLoading(false);
    }
  }, [competitionId, page, debouncedSearch, clubFilter, sortByGoals, user, t]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const selectedIds = selectedPlayers.map((p) => p.id);
  const selectionFull = selectedPlayers.length >= 4;
  const hasGwData = Object.keys(gwPickMap).length > 0 || selectedIds.length > 0;
  const showGoalsColumn = sortByGoals || players.some((p) => p.goals > 0);

  return (
    <>
      {/* ── Search / filter bar ── */}
      <div
        style={{
          ...card,
          marginBottom: "1.25rem",
          display: "flex",
          gap: "0.75rem",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          type="text"
          placeholder={t("playerSelection.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, maxWidth: "280px", flex: "1 1 180px" }}
        />
        <select
          value={clubFilter}
          onChange={(e) => {
            setClubFilter(e.target.value);
            setPage(1);
          }}
          style={selectStyle}
        >
          <option value="">{isInternational ? t("playerSelection.allCountries") : t("playerSelection.allClubs")}</option>
          {clubs.map((club) => (
            <option key={club} value={club}>
              {club}
            </option>
          ))}
        </select>
        <span
          style={{
            color: "var(--color-text-muted)",
            fontSize: "0.85rem",
            whiteSpace: "nowrap",
          }}
        >
          {t("playerSelection.totalPlayers", { count: total })}
        </span>
      </div>

      {error && (
        <p style={{ color: "var(--color-danger)", marginBottom: "0.75rem", fontSize: "0.9rem" }}>
          {error}
        </p>
      )}

      {/* ── Player table ── */}
      <div style={{ ...card, marginBottom: "1.25rem" }}>
        {loading ? (
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
            {t("playerSelection.loading")}
          </p>
        ) : players.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
            {t("playerSelection.noResults")}
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  {hasGwData && <th style={{ ...thStyle, width: "54px", textAlign: "center" }}>GW</th>}
                  <th style={thStyle}>{t("playerSelection.columns.name")}</th>
                  <th style={thStyle}>{isInternational ? t("playerSelection.columns.country") : t("playerSelection.columns.club")}</th>
                  <th style={thStyle}>{t("playerSelection.columns.position")}</th>
                  {showGoalsColumn && <th style={{ ...thStyle, textAlign: "right" }}>League Goals</th>}
                  <th style={{ ...thStyle, textAlign: "right", width: "120px" }}></th>
                </tr>
              </thead>
              <tbody>
                {players.map((player) => {
                  const isSelected = selectedIds.includes(player.id);
                  const isInOtherGw = player.id in gwPickMap;
                  const gwLabel = gwPickMap[player.id];
                  const isLockedOtherGw = lockedGwPlayerIds.includes(player.id);

                  return (
                    <tr
                      key={player.id}
                      style={{
                        background: isSelected
                          ? "var(--color-slot-filled-bg)"
                          : isInOtherGw
                          ? "var(--color-surface-alt)"
                          : "transparent",
                        opacity: isInOtherGw ? 0.8 : 1,
                      }}
                    >
                      {hasGwData && (
                        <td style={{ ...tdStyle, textAlign: "center", width: "54px" }}>
                          {isSelected ? (
                            <span style={{ color: "var(--color-slot-filled-border)", fontWeight: 700, fontSize: "0.8rem" }}>✓</span>
                          ) : gwLabel ? (
                            <span style={{
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              color: "var(--color-text-muted)",
                              background: "var(--color-border)",
                              borderRadius: "4px",
                              padding: "0.1rem 0.35rem",
                              whiteSpace: "nowrap",
                            }}>
                              {gwLabel}
                            </span>
                          ) : null}
                        </td>
                      )}
                      <td style={tdStyle}>{player.name}</td>
                      <td style={tdStyle}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                          <ClubBadge
                            config={isInternational ? getNationalityBadge(player.club) : getClubBadge(player.club)}
                            size={18}
                            title={player.club}
                          />
                          {player.club}
                        </span>
                      </td>
                      <td style={tdStyle}>{player.position ?? "—"}</td>
                      {showGoalsColumn && (
                        <td style={{
                          ...tdStyle,
                          textAlign: "right",
                          fontWeight: player.goals > 0 ? 700 : 400,
                          color: player.goals > 0 ? "var(--color-text)" : "var(--color-text-muted)",
                        }}>
                          {player.goals > 0 ? player.goals : "—"}
                        </td>
                      )}
                      <td style={{ ...tdStyle, textAlign: "right" }}>
                        {isSelected ? (
                          onRemoveCurrent && (
                            <button
                              onClick={() => onRemoveCurrent(player.id)}
                              style={{ ...actionBtnBase, background: "none", border: "1px solid var(--color-danger)", color: "var(--color-danger)" }}
                            >
                              {removeLabel ?? t("common.remove")}
                            </button>
                          )
                        ) : isInOtherGw ? (
                          isLockedOtherGw ? (
                            <span style={{ fontSize: "0.7rem", color: "var(--color-text-muted)" }}>🔒 {gwLabel}</span>
                          ) : onRemoveFromOtherGw || onMoveToThisGw ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", alignItems: "flex-end" }}>
                              {onMoveToThisGw && (
                                <button
                                  onClick={() => onMoveToThisGw(player.id)}
                                  style={{ ...actionBtnBase, background: "var(--color-accent)", color: "var(--color-btn-primary-text)" }}
                                >
                                  {moveToLabel ?? t("common.move")}
                                </button>
                              )}
                              {onRemoveFromOtherGw && (
                                <button
                                  onClick={() => onRemoveFromOtherGw(player.id)}
                                  style={{ ...actionBtnBase, background: "none", border: "1px solid var(--color-danger)", color: "var(--color-danger)" }}
                                >
                                  Remove from {gwLabel}
                                </button>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--color-text-muted)" }}>USED · {gwLabel}</span>
                          )
                        ) : !selectionFull ? (
                          <button
                            onClick={() => onSelect(player)}
                            style={{ ...actionBtnBase, background: "var(--color-accent)", color: "var(--color-btn-primary-text)" }}
                          >
                            {addLabel ?? t("common.add")}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            marginBottom: "1.25rem",
          }}
        >
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            style={{ ...secondaryBtn, opacity: page === 1 ? 0.5 : 1 }}
          >
            {t("playerSelection.pagination.previous")}
          </button>
          <span style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
            {t("playerSelection.pagination.pageOf", { page, total: totalPages })}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            style={{ ...secondaryBtn, opacity: page >= totalPages ? 0.5 : 1 }}
          >
            {t("playerSelection.pagination.next")}
          </button>
        </div>
      )}
    </>
  );
}

export default PlayerPicker;
